/*
 *  Kanlibs/KAN_VersionWatch.js
 *  Project: Kanoogi
 *
 *  Keeps a PLATFORM page on the build its server is running.
 *
 *  THE POLICY (Chris, 2026-10-11)
 *
 *  The platform - the km site (home, dashboard, forums, KanChat, the store, the
 *  library, the editors km serves) and the lobby - has no save games, so it must
 *  always run the CURRENT build, the way most web platforms force a refresh when
 *  they fall behind. GAMES are the opposite: a running game is pinned to its game
 *  bundle and must never reload into a newer build mid-game. So this module is
 *  loaded by km and lobby page entries ONLY - never by the IGSE game client
 *  (GSMProx/src/js/ISE_*) and never by a page that hosts a live game (the lobby's
 *  Squares canvas, game-sq). tests/version-watch-wiring.test.mjs holds both sides.
 *
 *  HOW IT DECIDES
 *
 *    page build    {sha, builtAt} baked into the bundle by webpack's DefinePlugin.
 *                  The same webpack run writes dist/.build-info.json, which the
 *                  server answers /api/version from (NodeLibs/KanBuildInfo.js), so a
 *                  page and its server can only disagree when they really are two
 *                  different builds.
 *    server build  GET /api/version (km) or /play/api/version (lobby), no-store.
 *    when          a slow jittered timer (~3 min), plus on focus / tab shown /
 *                  bfcache restore / back online (rate-limited to one per 30 s).
 *    what          DecideVersion() below - PURE, so tests/version-watch.test.mjs can
 *                  drive every case in node with no browser.
 *
 *  THE LOOP GUARDS (why this cannot ping-pong)
 *
 *  Production rolls km and the lobby with `rolling-action replace`, so for minutes
 *  at a time two builds answer behind one load balancer: one request reaches an old
 *  VM, the next a new one. Three rules keep that from becoming a reload loop:
 *
 *    1. A mismatch must PERSIST: the same foreign build, seen on consecutive checks
 *       with no sighting of our own build in between, spanning a minimum time.
 *    2. The persistence a build needs depends on its DIRECTION (by build time). A
 *       NEWER build needs 2 sightings a minute apart: that is a rollout. An OLDER
 *       or undatable build needs 3 sightings spanning 15 minutes with none of ours
 *       in between: that is a ROLLBACK (`deploy_prod.sh --rollback` promotes the
 *       older :prev-prod), and the tab follows the fleet back. A straggling old VM
 *       in a rollout lasts ~30-90 s (surge + min-ready 30 s) and answers between
 *       sightings of the new build, so it can never satisfy that - a tab on the new
 *       build is never dragged back by a rollout, only by a real rollback.
 *    3. Per tab (sessionStorage) we keep a small BOOK of the builds we already
 *       reloaded FOR, one record per build. A reload that landed back on an old VM
 *       may retry the same build only after a cool-down that doubles each time
 *       (2, 4, 8 ... capped at 30 min), and at most a handful of times per build;
 *       after that the banner offers "Reload now" and nothing reloads on its own.
 *       One record per build (not just the last one) is what bounds even a fleet
 *       stuck half on each build: each direction runs out on its own. If
 *       sessionStorage is unusable we cannot remember, so we never auto-reload.
 *
 *  BUSY GUARDS (when a reload would cost the user something)
 *
 *  A reload happens only when no guard reports busy. Built in: an editable control
 *  has focus; a text field holds text the user typed and has not sent; a video or
 *  audio element is playing; something is fullscreen or picture-in-picture; a
 *  dialog or modal is showing (a native <dialog>, anything declared aria-modal, or
 *  the site's div modals - see DialogReason); work is HELD in flight
 *  (Kanlibs/KAN_BusyHold.js - a recorded clip or an upload on its way); and, on
 *  editor pages, the editor's save contract (window.KAN_EDITOR - isDirty /
 *  saveState / busy, see KanoogiMain/src/shared/editor-nav.js) - an editor that has
 *  no contract cannot prove it is clean, so it counts as busy. Pages add their own
 *  with RegisterGuard(); a plain <script> that cannot import pushes onto
 *  window.KanVersionGuards instead:
 *
 *      (window.KanVersionGuards=window.KanVersionGuards||[]).push(['name',()=>reason]);
 *
 *  A guard returns a falsy value when idle, or a short reason when busy; a guard
 *  that THROWS counts as busy (we cannot prove the page is idle). While busy, a
 *  small non-blocking banner says Kanoogi has been updated and the page will reload
 *  when you are done, with a "Reload now" button, and the reload happens as soon as
 *  every guard clears. The wording names no direction: after a rollback the build
 *  it loads is an older one. Anything under [data-version-watch-ignore] is
 *  invisible to the field and dialog guards.
 *
 *  Local builds ('dev', 'local', blank) never auto-reload.
 *
 *  Copyright 2026 Kanoogi Inc. | All Rights Reserved.
 */

"use strict";

import { HeldReason } from './KAN_BusyHold.js';

export const DEFAULTS=Object.freeze(
	{
	intervalMs:3*60*1000,				// the slow timer
	jitter:0.35,						// +/- 35% so a fleet of tabs does not poll in lockstep
	minIntervalMs:30*1000,				// floor for the jittered timer
	firstCheckMs:[15*1000,45*1000],		// first check this long after boot (a just-loaded page is almost always current)
	focusGapMs:30*1000,					// focus/visibility checks at most this often
	confirmChecks:2,					// a NEWER build (a rollout): this many consecutive sightings...
	confirmSpanMs:60*1000,				// ...spanning at least this long
	backwardChecks:3,					// an OLDER or undatable build (a rollback): much stricter -
	backwardSpanMs:15*60*1000,			// a rollout's straggling old VM is gone in ~30-90 s
	retryCooldownMs:2*60*1000,			// after reloading for build X, wait this long before trying X again...
	maxCooldownMs:30*60*1000,			// ...doubling per attempt, up to this
	maxReloadsPerBuild:6,				// then stop: banner only
	bookMax:8,							// builds remembered in the per-tab reload book
	busyPollMs:2000,					// how often guards are re-read while a reload waits on them
	historyMax:12,						// server sightings kept
	});

const DEV_SHAS=new Set(['','dev','local','unknown','undefined','null']);
const STORAGE_PREFIX='kan.versionwatch.';
const GLOBAL_NAME='KanVersionWatch';
const QUEUE_NAME='KanVersionGuards';

/* ================================================================ */
/*  PURE - no window, no document, no clock. Everything here is     */
/*  driven by its arguments so node --test can cover it.            */
/* ================================================================ */

// NormSha() - a build sha as a comparable string.
export function NormSha(sha)
	{
	return(String(sha==null?'':sha).trim().toLowerCase());
	}

// IsDevBuild() - a local/unstamped build. These never auto-reload: every local
// webpack run would otherwise look like a "new version" to every open tab.
export function IsDevBuild(sha)
	{
	return(DEV_SHAS.has(NormSha(sha)));
	}

// ParseBuiltAt() - webpack's build time to epoch ms (NaN when unknown).
// Both configs bake 'YYYY-MM-DD HH:MM:SS' cut from toISOString(), i.e. UTC with
// the zone dropped - so it is parsed as UTC here, never as local time.
export function ParseBuiltAt(s)
	{
	const str=String(s==null?'':s).trim();
	if (!str) return(NaN);
	const m=/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.exec(str);
	if (m && !m[7]) return(Date.UTC(+m[1],+m[2]-1,+m[3],+m[4],+m[5],+m[6]));
	return(Date.parse(str));
	}

// CompareBuilds() - is the server's build newer (+1), older (-1), or not
// comparable (0: a build time is missing, or they are equal while the shas differ).
export function CompareBuilds(page, server)
	{
	const p=ParseBuiltAt(page && page.builtAt);
	const s=ParseBuiltAt(server && server.builtAt);
	if (!Number.isFinite(p) || !Number.isFinite(s) || p===s) return(0);
	return(s>p?1:-1);
	}

function Result(action, target, reason)
	{
	return({action, target:target||null, reason});
	}

// DecideVersion() - THE decision. Pure.
//
//   page        {sha, builtAt}            this page's own build
//   history     [{sha, builtAt, at}]      successful server answers, oldest first
//   busy        ''|reason                 BusyReason() of the page right now
//   reloaded    [{sha, at, count}]|null   the builds this tab already reloaded FOR (the
//                                         sessionStorage book; one bare record is accepted too)
//   canRemember bool                      false when sessionStorage is unusable
//   now         epoch ms
//   cfg         overrides for DEFAULTS
//
// Returns {action, target, reason}. action is one of:
//   'none'    nothing to do (current, local build, no data)
//   'wait'    a different build is showing but is not confirmed yet (or we are in
//             the retry cool-down) - show nothing, look again soon
//   'defer'   confirmed, but a guard is busy - banner, reload when the guards clear
//   'notify'  confirmed, but we may not reload on our own - the per-build reload
//             limit is spent, or there is no sessionStorage to remember with -
//             banner with "Reload now" only
//   'reload'  reload now (reason 'newer-build' for a rollout, 'older-or-undated-build'
//             for a rollback)
export function DecideVersion({page, history, busy, reloaded, canRemember=true, now, cfg}={})
	{
	const c={...DEFAULTS, ...(cfg||{})};
	const pageSha=NormSha(page && page.sha);
	if (IsDevBuild(pageSha)) return(Result('none',null,'dev-build'));

	const obs=(Array.isArray(history)?history:[])
		.filter((o)=>o && typeof o.sha==='string' && Number.isFinite(o.at));
	if (!obs.length) return(Result('none',null,'no-data'));

	const last=obs[obs.length-1];
	const sha=NormSha(last.sha);
	if (sha===pageSha) return(Result('none',null,'current'));
	if (IsDevBuild(sha)) return(Result('none',null,'server-dev-build'));

	// The trailing run of sightings of this SAME foreign build. A sighting of our
	// own build (or of a third one) in between breaks the run - which is exactly
	// what a mixed old/new rolling deploy looks like, and why it cannot confirm.
	let i=obs.length-1;
	while (i>0 && NormSha(obs[i-1].sha)===sha) i--;
	const run=obs.slice(i);
	const target={sha:last.sha, builtAt:String(last.builtAt||'')};

	const forward=(CompareBuilds(page,target)>0);
	const needChecks=forward?c.confirmChecks:c.backwardChecks;
	const needSpan=forward?c.confirmSpanMs:c.backwardSpanMs;
	const span=run[run.length-1].at-run[0].at;
	if (run.length<needChecks || span<needSpan)
		return(Result('wait',target,forward?'confirming-newer':'confirming-older-or-undated'));

	// Confirmed. A NEWER build is a rollout. An OLDER (or undatable) build that has
	// been the only answer for backwardSpanMs is a ROLLBACK: the fleet went back, so
	// the page goes back with it - "always the current build" includes the build the
	// fleet was rolled back TO, and a rollback usually means our build was the broken
	// one. Both directions go through the same per-build memory below.
	const rec=ReloadRecord(reloaded,sha);
	const tries=rec?(Number(rec.count)||0):0;
	if (tries>=c.maxReloadsPerBuild) return(Result('notify',target,'reload-limit'));
	if (rec)
		{
		const age=now-Number(rec.at);
		if (age>=0 && age<RetryCooldown(tries,c)) return(Result('wait',target,'retry-cooldown'));
		}
	if (!canRemember) return(Result('notify',target,'no-session-storage'));
	if (busy) return(Result('defer',target,'busy: '+busy));
	return(Result('reload',target,forward?'newer-build':'older-or-undated-build'));
	}

// BookOf() - the per-tab reload book as a list of records. Accepts the list, one
// bare record (an older shape), or anything else (an empty book).
function BookOf(book)
	{
	if (Array.isArray(book)) return(book.filter((r)=>r && typeof r==='object' && typeof r.sha==='string'));
	if (book && typeof book==='object' && typeof book.sha==='string') return([book]);
	return([]);
	}

// ReloadRecord() - this tab's record for build `sha`, or null if it never reloaded for it.
export function ReloadRecord(book, sha)
	{
	const want=NormSha(sha);
	if (!want) return(null);
	const list=BookOf(book);
	for (let i=list.length-1;i>=0;i--) if (NormSha(list[i].sha)===want) return(list[i]);
	return(null);
	}

// RetryCooldown() - how long after the `tries`-th reload for one build before the
// next may happen: retryCooldownMs, doubling per attempt, capped at maxCooldownMs.
export function RetryCooldown(tries, cfg)
	{
	const c={...DEFAULTS, ...(cfg||{})};
	const n=Math.max(1,Number(tries)||1);
	return(Math.min(c.maxCooldownMs, c.retryCooldownMs*Math.pow(2,n-1)));
	}

// NoteReload() - the book (sessionStorage) after reloading for `target` at `now`:
// that build's record counts one more attempt and moves to the end; every OTHER
// build's record is kept as it was. Keeping them (instead of only the last) is the
// ping-pong bound - a tab bounced between two builds spends both builds' limits
// instead of resetting one each time. The newest bookMax builds are kept.
export function NoteReload(book, target, now, cfg)
	{
	const c={...DEFAULTS, ...(cfg||{})};
	const sha=String(target && target.sha || '');
	const prev=ReloadRecord(book,sha);
	const list=BookOf(book).filter((r)=>NormSha(r.sha)!==NormSha(sha));
	list.push({sha, builtAt:String(target && target.builtAt || ''), at:now, count:(prev?(Number(prev.count)||0):0)+1});
	return(list.slice(-c.bookMax));
	}

// NextCheckDelay() - the jittered slow-timer delay.
export function NextCheckDelay(cfg, rand=Math.random)
	{
	const c={...DEFAULTS, ...(cfg||{})};
	const f=1+c.jitter*(2*rand()-1);
	return(Math.max(c.minIntervalMs, Math.round(c.intervalMs*f)));
	}

// ConfirmDelay() - when a NEWER build has been seen once, look again just past
// the confirmation span instead of waiting out the whole slow timer.
export function ConfirmDelay(cfg, rand=Math.random)
	{
	const c={...DEFAULTS, ...(cfg||{})};
	return(Math.round(c.confirmSpanMs*(1+0.5*rand()))+1000);
	}

// AppendHistory() - add one server answer, keeping the newest historyMax.
export function AppendHistory(history, answer, at, cfg)
	{
	const c={...DEFAULTS, ...(cfg||{})};
	const next=(Array.isArray(history)?history:[]).concat([{sha:String(answer && answer.sha || ''), builtAt:String(answer && answer.builtAt || ''), at}]);
	return(next.slice(-c.historyMax));
	}

// BusyReason() - the first busy guard's reason, or '' when every guard is idle.
// `guards` is any iterable of [name, fn]. A guard returning true reports its own
// name; a string is the reason; anything falsy is idle. A guard that THROWS counts
// as busy - we cannot prove the page is idle, and a missed reload costs nothing
// while a wrong one costs the user their work.
export function BusyReason(guards)
	{
	for (const [name,fn] of (guards||[]))
		{
		if (typeof fn!=='function') continue;
		let v;
		try { v=fn(); }
		catch (err) { return(`${name}: guard failed`); }
		if (v) return((typeof v==='string')?v:String(name));
		}
	return('');
	}

// EditorBusyReason() - the editor save contract (window.KAN_EDITOR, documented in
// KanoogiMain/src/shared/editor-nav.js) as a busy reason.
//   requireContract: on an editor page, an editor that cannot say whether it is
//   clean (no KAN_EDITOR, or one with neither isDirty nor saveState) counts as busy.
export function EditorBusyReason(ed, {requireContract=false}={})
	{
	try
		{
		if (!ed || typeof ed!=='object')
			return(requireContract?'editor has no save state':'');
		if (typeof ed.busy==='function')
			{
			const b=ed.busy();
			if (b) return((typeof b==='string')?b:'editor busy');
			}
		const hasDirty=(typeof ed.isDirty==='function');
		const hasState=(typeof ed.saveState==='function');
		if (!hasDirty && !hasState)
			return(requireContract?'editor has no save state':'');
		if (hasDirty && ed.isDirty()) return('unsaved changes');
		if (hasState)
			{
			const st=ed.saveState();
			const state=String((st && st.state) || '').toLowerCase();
			if (state==='saving') return('saving');
			if (state==='unsaved') return('unsaved changes');
			if (state==='error') return('last save failed');
			}
		return('');
		}
	catch (err) { return('editor state unreadable'); }
	}

/* ================================================================ */
/*  DOM guards - read a document, take nothing else.                */
/* ================================================================ */

const TEXT_INPUT_TYPES=new Set(['','text','search','email','url','tel','password','number']);

// IsTextEntry() - a control a person types into.
export function IsTextEntry(el)
	{
	if (!el || el.nodeType!==1) return(false);
	if (el.isContentEditable) return(true);
	const tag=String(el.tagName||'').toUpperCase();
	if (tag==='TEXTAREA') return(!el.disabled && !el.readOnly);
	if (tag==='INPUT')
		{
		const type=String(el.getAttribute && el.getAttribute('type') || '').toLowerCase();
		return(TEXT_INPUT_TYPES.has(type) && !el.disabled && !el.readOnly);
		}
	return(false);
	}

function Ignored(el)
	{
	try { return(!!(el && el.closest && el.closest('[data-version-watch-ignore]'))); }
	catch (err) { return(false); }
	}

function FieldText(el)
	{
	if (!el) return('');
	if (el.isContentEditable) return(String(el.textContent||''));
	return(String(el.value==null?'':el.value));
	}

// FocusedEntryReason() - someone is in a text control or a <select> right now.
// Only while the window itself has focus: activeElement stays on an autofocused
// (or abandoned) field after the person switches away, and an EMPTY field in a
// window nobody is looking at is no reason to keep an old build running. A field
// with typed text is covered by TypedTextReason whether focused or not.
export function FocusedEntryReason(doc)
	{
	const el=doc && doc.activeElement;
	if (!el || Ignored(el)) return('');
	if (typeof doc.hasFocus==='function' && !doc.hasFocus()) return('');
	if (IsTextEntry(el)) return('typing');
	if (String(el.tagName||'').toUpperCase()==='SELECT') return('choosing');
	return('');
	}

// TypedTextReason() - a text control the user typed into still holds text. `touched`
// is the set of elements that have seen a real input event (programmatic value
// changes fire none, so pre-filled fields do not count). Detached elements are
// dropped from the set as a side effect, so it cannot grow without bound.
export function TypedTextReason(touched)
	{
	if (!touched) return('');
	for (const el of Array.from(touched))
		{
		if (!el || !el.isConnected) { touched.delete(el); continue; }
		if (Ignored(el)) continue;
		if (FieldText(el).trim()) return('unsent text');
		}
	return('');
	}

// MediaReason() - audio/video playing, or picture-in-picture. A LOOPING element is
// ambient (menu music, a background clip) and never ends, so it does not count -
// otherwise a page with one could never update.
export function MediaReason(doc)
	{
	if (!doc) return('');
	if (doc.pictureInPictureElement) return('picture-in-picture');
	const media=doc.querySelectorAll ? doc.querySelectorAll('video,audio') : [];
	for (const m of Array.from(media))
		if (m && !m.paused && !m.ended && !m.loop) return('media playing');
	return('');
	}

// FullscreenReason() - a presentation or a video is fullscreen; a reload drops it.
export function FullscreenReason(doc)
	{
	if (!doc) return('');
	return((doc.fullscreenElement || doc.webkitFullscreenElement)?'fullscreen':'');
	}

// IsShown() - is this element actually on screen: connected, and neither it nor an
// ancestor is [hidden] or display:none, nor is it visibility:hidden. Uses
// checkVisibility() where the browser has it; otherwise walks the ancestors with
// getComputedStyle (an inline style is the last resort, for a document with no view).
export function IsShown(el)
	{
	try
		{
		if (!el || el.nodeType!==1 || el.isConnected===false) return(false);
		if (typeof el.checkVisibility==='function')
			return(!!el.checkVisibility({checkVisibilityCSS:true, visibilityProperty:true}));
		const view=el.ownerDocument && el.ownerDocument.defaultView;
		const styleOf=(e)=>((view && typeof view.getComputedStyle==='function')?view.getComputedStyle(e):(e.style||null));
		for (let e=el; e && e.nodeType===1; e=e.parentElement)
			{
			if (e.hidden) return(false);
			const s=styleOf(e);
			if (s && s.display==='none') return(false);
			}
		const own=styleOf(el);
		return(!(own && own.visibility==='hidden'));
		}
	catch (err) { return(false); }
	}

// The modals a person can be mid-decision or mid-entry in. A native <dialog> is the
// exception on km and the lobby; nearly all of them are divs: ShowConfirmModal /
// ShowNotice (.kmodal-backdrop), the forum and video-recorder modals (.modal), and
// anything that declares itself a modal dialog (profile's crop modal, the Orchestral
// capture and piano studios, the Universe editor's conflict and derive panels, the
// draft-conflict alert). They stay in the DOM while closed, so only a SHOWN one counts.
export const MODAL_SELECTOR='dialog[open],[role="dialog"][aria-modal="true"],[role="alertdialog"][aria-modal="true"],.kmodal-backdrop,.modal';

// DialogReason() - a modal is showing (mid-decision, or holding what was typed into it).
export function DialogReason(doc)
	{
	try
		{
		if (!doc || typeof doc.querySelectorAll!=='function') return('');
		for (const el of Array.from(doc.querySelectorAll(MODAL_SELECTOR)))
			if (!Ignored(el) && IsShown(el)) return('dialog open');
		return('');
		}
	catch (err) { return(''); }
	}

/* ================================================================ */
/*  Browser wiring                                                  */
/* ================================================================ */

// Api() - the one per-page registry, shared by every copy of this module on the
// page (a webpack bundle and a static /shared script can both load it) and by
// plain scripts, which reach it through window.KanVersionWatch / KanVersionGuards.
function Api()
	{
	if (typeof window==='undefined') return(null);
	let api=window[GLOBAL_NAME];
	if (api && api.guards instanceof Map && typeof api.guard==='function') return(api);
	const guards=new Map();
	api={
		guards,
		started:false,
		guard(name, fn)
			{
			const key=String(name||'guard');
			if (typeof fn!=='function') return(()=>{});
			guards.set(key,fn);
			return(()=>{ if (guards.get(key)===fn) guards.delete(key); });
			},
		};
	window[GLOBAL_NAME]=api;
	return(api);
	}

// RegisterGuard() - add a busy guard for this page. Returns an unregister function.
//   RegisterGuard('kanchat-composer', ()=>composer.value.trim() ? 'unsent message' : '');
export function RegisterGuard(name, fn)
	{
	const api=Api();
	return(api?api.guard(name,fn):()=>{});
	}

function StorageGet(key)
	{
	try { const v=window.sessionStorage.getItem(STORAGE_PREFIX+key); return(v?JSON.parse(v):null); }
	catch (err) { return(null); }
	}
function StorageSet(key, value)
	{
	try { window.sessionStorage.setItem(STORAGE_PREFIX+key,JSON.stringify(value)); return(true); }
	catch (err) { return(false); }
	}
// StorageWorks() - write, read back, remove. A private window, blocked site data
// or a full quota all fail here, and then the page never reloads on its own.
function StorageWorks()
	{
	try
		{
		const k=STORAGE_PREFIX+'probe';
		window.sessionStorage.setItem(k,'1');
		const ok=window.sessionStorage.getItem(k)==='1';
		window.sessionStorage.removeItem(k);
		return(ok);
		}
	catch (err) { return(false); }
	}

// BannerMessage() - the banner's words. Direction-neutral on purpose: after a
// rollback the build this tab moves to is an OLDER one, and "a new version" would
// be a small lie told exactly when something has gone wrong.
export function BannerMessage(kind)
	{
	return((kind==='defer')
		?'<strong>Kanoogi has been updated.</strong> This page will reload when you’re done.'
		:'<strong>Kanoogi has been updated.</strong> Reload to pick it up.');
	}

const BANNER_CSS=`
:host{all:initial}
.bar{position:fixed;z-index:2147483000;left:16px;right:16px;bottom:calc(16px + env(safe-area-inset-bottom,0px));
	max-width:520px;margin:0;box-sizing:border-box;display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px;
	padding:10px 10px 10px 16px;border-radius:12px;background:#10151d;color:#eef2f7;
	border:1px solid rgba(140,200,255,.35);box-shadow:0 8px 28px rgba(0,0,0,.45);
	font:14px/1.4 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;pointer-events:auto}
.msg{flex:1 1 220px;min-width:0}
.msg strong{font-weight:600}
.btns{display:flex;gap:8px;align-items:center;margin-left:auto}
button{font:inherit;min-height:44px;min-width:44px;border-radius:10px;cursor:pointer;touch-action:manipulation}
.now{padding:0 16px;background:#3d8bfd;color:#fff;border:0;font-weight:600}
.x{padding:0;background:transparent;color:#c9d3df;border:1px solid rgba(255,255,255,.22);font-size:20px;line-height:1}
.now:focus-visible,.x:focus-visible{outline:3px solid #ffd166;outline-offset:2px}
@media (hover:hover){.now:hover{background:#5a9cff}.x:hover{color:#fff;border-color:rgba(255,255,255,.5)}}
@media (min-width:768px){.bar{right:auto;width:max-content;max-width:min(520px,calc(100vw - 32px))}}
@media print{.bar{display:none}}
`;

// StartVersionWatch() - boot the watcher for this page. Idempotent per page.
//
//   service    'km' | 'lobby' - namespaces the per-tab memory
//   endpoint   where the server answers {sha, builtAt}
//   build      {sha, builtAt} - this page's own build (DefinePlugin)
//   mode       'site' (default) | 'editor' - editor pages trust the KAN_EDITOR
//              contract instead of the typed-text heuristic, and REQUIRE it
//   cfg        overrides for DEFAULTS (tests / diagnostics)
export function StartVersionWatch({service='site', endpoint='/api/version', build={}, mode='site', cfg}={})
	{
	if (typeof window==='undefined' || typeof document==='undefined') return(null);
	const api=Api();
	if (api.started) return(api);
	api.started=true;

	const c={...DEFAULTS, ...(cfg||{})};
	const page={sha:String(build && build.sha || ''), builtAt:String(build && build.builtAt || '')};
	const editorMode=(mode==='editor');
	const recKey=`${service}.reloaded`;
	const dismissKey=`${service}.dismissed`;
	const canRemember=StorageWorks();
	const touched=new Set();

	let history=[];
	let lastDecision=Result('none',null,'not-checked');
	let lastCheckAt=0;
	let inflight=null;
	let timer=0;
	let busyTimer=0;
	let banner=null;

	const log=(...a)=>{ try { if (window.localStorage && window.localStorage.getItem('kan.versionwatch.debug')==='1') console.info('[VersionWatch]',...a); } catch (err) {} };

	// Every user keystroke into a text control marks it (programmatic writes fire no
	// input event). Capture phase, passive: this never interferes with the page.
	document.addEventListener('input',(e)=>{ const t=e.target; if (IsTextEntry(t)) touched.add(t); },{capture:true, passive:true});

	function Guards()
		{
		const list=[
			['focus',()=>FocusedEntryReason(document)],
			['media',()=>MediaReason(document)],
			['fullscreen',()=>FullscreenReason(document)],
			['dialog',()=>DialogReason(document)],
			['held',()=>HeldReason(window)],
			['editor',()=>EditorBusyReason(window.KAN_EDITOR,{requireContract:editorMode})],
			];
		if (!editorMode) list.push(['typed-text',()=>TypedTextReason(touched)]);
		for (const entry of api.guards) list.push(entry);
		const queue=window[QUEUE_NAME];
		if (Array.isArray(queue)) for (const q of queue) if (Array.isArray(q)) list.push([q[0],q[1]]);
		return(list);
		}

	function Busy() { return(BusyReason(Guards())); }

	function Evaluate()
		{
		const busy=Busy();
		const d=DecideVersion({page, history, busy, reloaded:StorageGet(recKey), canRemember, now:Date.now(), cfg:c});
		lastDecision=d;
		Apply(d);
		return(d);
		}

	function Apply(d)
		{
		if (d.action==='reload')
			{
			StopBusyPoll();
			StorageSet(recKey,NoteReload(StorageGet(recKey),d.target,Date.now(),c));
			log('reloading for',d.target,d.reason);
			HideBanner();
			try { window.location.reload(); } catch (err) {}
			return;
			}
		if (d.action==='defer')
			{
			ShowBanner('defer',d.target);
			StartBusyPoll();
			return;
			}
		StopBusyPoll();
		if (d.action==='notify') ShowBanner('notify',d.target);
		else HideBanner();
		}

	function StartBusyPoll()
		{
		if (busyTimer) return;
		busyTimer=window.setInterval(Evaluate,c.busyPollMs);
		}
	function StopBusyPoll()
		{
		if (!busyTimer) return;
		window.clearInterval(busyTimer);
		busyTimer=0;
		}

	function Schedule(ms)
		{
		if (timer) window.clearTimeout(timer);
		timer=window.setTimeout(()=>{ timer=0; Check('timer'); },ms);
		}

	async function Check(why)
		{
		if (inflight) return(inflight);
		const now=Date.now();
		if (why==='focus' && now-lastCheckAt<c.focusGapMs) return(lastDecision);
		lastCheckAt=now;
		inflight=(async ()=>
			{
			try
				{
				const r=await fetch(endpoint,{cache:'no-store', credentials:'omit', headers:{'Accept':'application/json'}});
				if (r.ok)
					{
					const j=await r.json();
					if (j && typeof j.sha==='string') history=AppendHistory(history,j,Date.now(),c);
					}
				}
			catch (err) { /* offline / blocked: no sighting, nothing changes */ }
			const d=Evaluate();
			// A NEWER build seen once: confirm it soon. Everything else: the slow timer.
			Schedule((d.action==='wait' && d.reason==='confirming-newer')?ConfirmDelay(c):NextCheckDelay(c));
			log(why,d);
			return(d);
			})();
		try { return(await inflight); }
		finally { inflight=null; }
		}

	function ShowBanner(kind, target)
		{
		const sha=String(target && target.sha || '');
		if (StorageGet(dismissKey)===sha) { HideBanner(); return; }
		if (!document.body) return;
		if (!banner)
			{
			const host=document.createElement('div');
			host.setAttribute('data-kan-version-banner','');
			host.setAttribute('data-version-watch-ignore','');
			const root=host.attachShadow ? host.attachShadow({mode:'open'}) : host;
			root.innerHTML=`<style>${BANNER_CSS}</style>`
				+`<div class="bar" role="status" aria-live="polite">`
				+`<span class="msg"></span>`
				+`<span class="btns"><button type="button" class="now">Reload now</button>`
				+`<button type="button" class="x" aria-label="Hide this message">×</button></span></div>`;
			root.querySelector('.now').addEventListener('click',()=>
				{
				StorageSet(recKey,NoteReload(StorageGet(recKey),lastDecision.target||target,Date.now(),c));
				window.location.reload();
				});
			root.querySelector('.x').addEventListener('click',()=>
				{
				StorageSet(dismissKey,String((lastDecision.target && lastDecision.target.sha) || sha));
				HideBanner();
				});
			banner={host, root, kind:''};
			}
		if (banner.kind!==kind)
			{
			banner.root.querySelector('.msg').innerHTML=BannerMessage(kind);
			banner.kind=kind;
			}
		if (!banner.host.isConnected) document.body.appendChild(banner.host);
		PlaceBanner();
		}

	// PlaceBanner() - sit ABOVE any other fixed chrome the banner would cover along
	// the bottom edge (the cookie notice, an editor's bottom pill or help strip, a
	// fixed composer) instead of hiding it. Re-run on every show - the busy poll
	// re-shows every 2 s - and on resize, so the banner drops back down once that
	// chrome goes away. A fixed layer taller than half the screen (an editor's
	// full-page canvas) is a backdrop, not chrome; and if clearing the chrome would
	// push the banner past the middle of the screen it stays put (dismissable).
	function PlaceBanner()
		{
		if (!banner || !banner.host.isConnected || typeof document.elementsFromPoint!=='function') return;
		const bar=banner.root.querySelector('.bar');
		bar.style.bottom='';
		const vh=window.innerHeight, vw=window.innerWidth;
		const r=bar.getBoundingClientRect();
		let lift=0;
		const seen=new Set();
		for (const x of [r.left+6,(r.left+r.right)/2,r.right-6])
			for (const y of [r.top+6,(r.top+r.bottom)/2,r.bottom-6])
				for (const el of document.elementsFromPoint(Math.max(0,Math.min(vw-1,x)),Math.max(0,Math.min(vh-1,y))))
					{
					let f=el;
					while (f && f!==document.body && f!==document.documentElement && f!==banner.host)
						{
						const pos=getComputedStyle(f).position;
						if (pos==='fixed' || pos==='sticky') break;
						f=f.parentElement;
						}
					if (!f || f===document.body || f===document.documentElement || f===banner.host || seen.has(f)) continue;
					seen.add(f);
					const fr=f.getBoundingClientRect();
					if (fr.height>vh/2 || fr.top<=0 || fr.bottom<r.top) continue;
					lift=Math.max(lift,vh-fr.top);
					}
		if (lift && vh-lift-8-r.height>=vh*0.4) bar.style.bottom=`calc(${Math.round(lift)+8}px + env(safe-area-inset-bottom, 0px))`;
		}

	function HideBanner()
		{
		if (banner && banner.host.isConnected) banner.host.remove();
		}
	window.addEventListener('resize',()=>{ if (banner && banner.host.isConnected) PlaceBanner(); });

	const OnFocus=()=>{ if (document.visibilityState!=='hidden') Check('focus'); };
	document.addEventListener('visibilitychange',OnFocus);
	window.addEventListener('focus',OnFocus);
	window.addEventListener('online',OnFocus);
	window.addEventListener('pageshow',(e)=>{ if (e && e.persisted) Check('focus'); });

	const [lo,hi]=c.firstCheckMs;
	Schedule(Math.round(lo+(hi-lo)*Math.random()));

	// Diagnostics, for a console or a browser test: nothing here reloads by itself
	// except check(), which runs the real decision.
	api.page=page;
	api.mode=editorMode?'editor':'site';
	api.check=()=>Check('manual');
	api.status=()=>({page, mode:api.mode, history:history.slice(), decision:lastDecision, busy:Busy(), reloaded:StorageGet(recKey), canRemember});
	api.preview=(kind='defer')=>{ ShowBanner(kind==='notify'?'notify':'defer',{sha:'preview'}); return(banner && banner.host); };
	api.hide=HideBanner;
	return(api);
	}
